import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Event, NavigationCancel, NavigationEnd, NavigationError, ResolveStart, Router } from '@angular/router';

import { PrettyUrlsConfig } from '@frontend/sports/common/client-config-data-access';
import { CustomLoadingIndicatorService, ILoadingIndicatorHandler } from '@frontend/sports/loading-indicator-feature';
import { HtmlNode } from '@frontend/vanilla/core';
import { combineLatest, distinctUntilChanged, distinctUntilKeyChanged, filter, map, scan, share, startWith, tap } from 'rxjs';

import { AdaptiveLayoutService } from '../layout/adaptive-layout.service';
import { IBetSlipRepositoryResult } from '../my-bets-base/models/my-bets-viewmodels';
import { useResolverModel } from '../router/router.resolver';

@Component({
    selector: 'ms-my-bets-route',
    templateUrl: 'my-bets-route.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyBetsRouteComponent {
    public adaptiveLayout = inject(AdaptiveLayoutService);
    private router = inject(Router);

    private readonly navigationComplete$ = this.router.events.pipe(
        filter((event: Event) => [ResolveStart, NavigationEnd, NavigationCancel, NavigationError].some((eventType) => event instanceof eventType)),
        map((event) => [NavigationEnd, NavigationCancel, NavigationError].some((eventType) => event instanceof eventType)),
        startWith(true),
        distinctUntilChanged(),
    );
    private readonly repositoryResult$ = useResolverModel<IBetSlipRepositoryResult>();

    private readonly routeData$ = combineLatest([this.navigationComplete$, this.repositoryResult$]).pipe(
        map(([navigationComplete, model]) => ({ navigationComplete, model, isRouteDataLoaded: true })),
        scan<{ model: IBetSlipRepositoryResult; navigationComplete: boolean; isRouteDataLoaded: boolean }>((prev, { navigationComplete, model }) => {
            return { model, navigationComplete, isRouteDataLoaded: model.listType !== prev.model.listType && navigationComplete };
        }),
        distinctUntilKeyChanged('isRouteDataLoaded'),
        share(),
    );

    protected readonly routeData = toSignal(this.routeData$);

    constructor(loadingIndicatorService: CustomLoadingIndicatorService, urlConfig: PrettyUrlsConfig, destroyRef: DestroyRef, html: HtmlNode) {
        let loadingIndicator: ILoadingIndicatorHandler | null = null;
        this.routeData$
            .pipe(
                map(({ isRouteDataLoaded }) => isRouteDataLoaded),
                startWith(false),
                tap((isRouteDataLoaded) => {
                    if (isRouteDataLoaded && loadingIndicator) {
                        loadingIndicator.done();
                    } else if (this.router.url.includes(urlConfig.translations.myBets)) {
                        loadingIndicator = loadingIndicatorService.start('');
                    }
                }),
                takeUntilDestroyed(),
            )
            .subscribe();

        destroyRef.onDestroy(() => {
            if (loadingIndicator) {
                loadingIndicator.done();
            }
            html.setCssClass('my-bets', false);
        });
    }
}
